import * as React from 'react';
import { connect } from 'react-redux';
import { Formik, Form, FormikProps } from 'formik';
import * as Yup from 'yup';

import * as Edge from '../../core';
import { AppState } from '../../store';
import Error from '../../components/global/error';
import PageTitle from '../../components/pageTitle';
import AppPageContainer from '../../components/pageContainer';
import FormField from '../../components/formField';
import Input from '../../components/global/input';
import Loading from '../../components/loading';
import { ExerciseService } from '../../services/exerciseService';
import history from '../../services/history';
import { switchToToken } from '../../store/login/actions';
import { AuthenticationService } from '../../services/authenticationService';

import './index.scss';

export interface GuardVisionFormValues {
	lowestLine: string;
	certified: boolean;
}

export interface GuardVisionProps {
	switchToToken: (token: string, redirectPath: string) => Promise<void>;
	teamId: string;
	userId: string;
}

const validationSchema = Yup.object().shape({
	lowestLine: Yup.string().required('Please select the lowest line you were able to read'),
	certified: Yup.boolean().oneOf([true], 'You must certify that the information is accurate'),
});

const initialValues: GuardVisionFormValues = {
	lowestLine: '',
	certified: false,
};

export const GuardVision: React.FC<GuardVisionProps> = ({ switchToToken: switchToTokenProp, teamId, userId }) => {
	const returnToScout = React.useCallback(async () => {
		const token = AuthenticationService.restoreToken();
		if (token) {
			await switchToTokenProp(token, '/dashboard/scout');
		} else {
			history.push('/dashboard/scout');
		}
	}, [switchToTokenProp]);
	return (
		<>
			<PageTitle title="GuardVision Screening" />
			<AppPageContainer>
				<div className="guardvision-container">
					<div className="guardvision-left">
						<h2 className="guardvision-header">SCREENING</h2>
						<h3 className="guardvision-subheader">
							GUARDVISION SCREENING INSTRUCTIONS - <b>TO BE COMPLETED BY SUPERVISOR</b>
						</h3>
						<div className="guardvision-instructions">
							<ol>
								<li>
									Download and print off{' '}
									<a
										href="https://www.vizualedge.com/s/GuardVision-Chart-A.pdf"
										target="_blank"
										rel="noopener noreferrer"
									>
										Chart A
									</a>{' '}
									or{' '}
									<a
										href="https://www.vizualedge.com/s/GuardVision-Chart-B.pdf"
										target="_blank"
										rel="noopener noreferrer"
									>
										Chart B
									</a>
								</li>
								<li>
									Using a tape measure, measure out 10 feet from the vision chart (A or B) and the
									spot on the floor where you intend to have the lifeguard test from. The chart should
									be eye-level in a well-lit room and can either be held or taped against a wall.
								</li>
								<li>
									Ask the lifeguard to read the lowest line of letters that they can identify from
									either chart A or B (minimum 3 or more letters correctly identified)
								</li>
								<li>
									If the lifeguard cannot identify 3 or more letters for a given line, ask them to
									attempt the line above and repeat.
								</li>
								<li>
									Have the lifeguard enter the lowest line they were able to read on the right and
									click "Submit Score".
								</li>
							</ol>
							<div className="guardvision-video">
								<iframe
									className="video"
									src={Edge.Constants.VIDEOS.GUARDVISION}
									title="Instructions"
									{...{ referrerPolicy: 'strict-origin-when-cross-origin' } as any}
								/>
							</div>
						</div>
					</div>
					<div className="guardvision-divider" />
					<div className="guardvision-right">
						<h2 className="guardvision-form-header">TO BE COMPLETED BY THE LIFEGUARD BEING SCREENED</h2>
						<Formik
							initialValues={initialValues}
							validationSchema={validationSchema}
							onSubmit={async (values, actions) => {
								await ExerciseService.completeGuardVisionScreening({
									score: parseInt(values.lowestLine, 10),
									teamId,
									userId,
								});
								actions.setSubmitting(false);

								await returnToScout();
							}}
							render={(props: FormikProps<GuardVisionFormValues>) => (
								<Form>
									<FormField
										component="select"
										name="lowestLine"
										label="Lowest Line You Were Able to Read"
									>
										<option value="">Lowest Line You Were Able to Read</option>
										<option value="0">0 (none)</option>
										<option value="1">1</option>
										<option value="2">2</option>
										<option value="3">3</option>
										<option value="4">4</option>
										<option value="5">5</option>
										<option value="6">6</option>
										<option value="7">7</option>
										<option value="8">8</option>
										<option value="9">9</option>
									</FormField>
									<div className="guardvision-certification">
										<FormField
											type="checkbox"
											name="certified"
											label="I certify that I, the lifeguard being screened, accurately entered the above information to the best of my ability."
											description="I certify that I, the lifeguard being screened, accurately entered the above information to the best of my ability."
										/>
									</div>
									{props.status && <Error>{props.status}</Error>}
									{props.isSubmitting && <Loading buttonLoader />}
									<Input type="submit" disabled={props.isSubmitting} value="SUBMIT SCORE" />
								</Form>
							)}
						/>
					</div>
				</div>
			</AppPageContainer>
		</>
	);
};

function mapStateToProps(state: AppState) {
	const { teamState, loginState } = state;
	const { teams } = teamState;
	const teamId = teams && teams.length > 0 ? teams[0].id : '';
	const userId = loginState.decodedToken ? loginState.decodedToken.sub : '';
	return {
		teamId,
		userId,
	};
}

export default connect(
	mapStateToProps,
	{
		switchToToken,
	}
)(GuardVision);
